import Fragment from "./fragment";
import FragmentDataType from "../enums/fragment-data-type";
import FragmentSummary from "./fragment-summary";
import { playerReducer } from "../../../storage/reducers";
import FootballState from "../state/football-state";
import { FragmentMessages } from "../../common/fragment-messages";
import PassResultType from "../enums/pass-result-type";
import InterceptedByPositionPref from "./position-pref/intercepted-by-position-pref";
import PatResultType from "../enums/pat-result-type";
import ReturnedByDefaultValue from "./default-value/returned-by-default-value";

export default class PassIntercepted extends Fragment {
    constructor(interceptedBy, interceptedAt, returnedBy, returnedAt) {
        super();

        this.interceptedBy = interceptedBy;
        this.interceptedAt = interceptedAt;
        this.returnedBy = returnedBy;
        this.returnedAt = returnedAt;
        this.passResultCode = PassResultType.INTERCEPTED.code;
        this.patCode = PatResultType.INTERCEPTED.code;

        this.requiredData = [
            {
                name: "interceptedBy",
                type: FragmentDataType.Player,
                appliesToOpponent: () => true,
                displayName: "Intercepted By",
                title: (team) => `Intercepted By: ${team.teamName}`,
                subtitle: (team) =>
                    `Select a player
                     from ${team.teamName} or enter the roster number`,
                isEditable: true,
                positionPref: (play) =>
                    new InterceptedByPositionPref(play, this).resolve()
            },
            {
                name: "interceptedAt",
                type: FragmentDataType.FieldPosition,
                displayName: "Intercepted At",
                title: "Intercepted At",
                subtitle: FragmentMessages.FB_TEAM_FIELD_POSITION
            },
            {
                name: "returnedBy",
                type: FragmentDataType.Player,
                appliesToOpponent: () => true,
                displayName: "Returned By",
                title: (team) => `Returned By: ${team.teamName}`,
                subtitle: (team) =>
                    `Select a player
                     from ${team.teamName} or enter the roster number`,
                isEditable: true,
                defaultValue: ({ play, summaryContext }) =>
                    new ReturnedByDefaultValue(
                        play,
                        this,
                        summaryContext
                    ).resolve()
            },
            {
                name: "returnedAt",
                type: FragmentDataType.FieldPosition,
                displayName: "At",
                title: "At",
                subtitle: FragmentMessages.FB_TEAM_FIELD_POSITION,
                requiredAfterNextState: [
                    FootballState.Tackle,
                    FootballState.OutOfBounds
                ],
                showEndZoneYards: (gameConfig) =>
                    gameConfig.general.includeEndZoneYardage
            }
        ];
    }

    changesPossession() {
        return true;
    }

    getPlayers() {
        return [this.interceptedBy, this.returnedBy];
    }

    getFieldPosition() {
        return this.interceptedAt;
    }

    summarize(summaryContext, stringifier) {
        const message = `intercepted by 
		${stringifier.firstInitial(this.interceptedBy)} 
		at the ${stringifier.niceFieldPosition(
            summaryContext.gameConfig.general.fieldLength,
            this.interceptedAt,
            summaryContext.homeTeam?.abbrName,
            summaryContext.awayTeam?.abbrName
        )}, 
		${stringifier.firstInitial(this.returnedBy)} 
		returned 
		for ${stringifier.gain(
            Object.assign(summaryContext, {
                homeTeamHasPossession:
                    summaryContext.homeTeamId === this.interceptedBy?.teamId
            }),
            this.interceptedAt,
            this.returnedAt
        )}`;
        return new FragmentSummary(message);
    }

    updateSummaryContext(summaryContext) {
        summaryContext.homeTeamHasPossession =
            this.interceptedBy?.teamId === summaryContext.homeTeamId;
        summaryContext.fieldPosition = this.returnedAt;
        summaryContext.playerInPossession = this.interceptedBy;
    }

    getFragmentTeamId() {
        return this.interceptedBy?.teamId;
    }

    toJSON() {
        return {
            ...super.toJSON(),
            interceptedBy: playerReducer(this.interceptedBy),
            returnedBy: playerReducer(this.returnedBy)
        };
    }
}
